"use client";

import { useEffect, useState } from "react";
import { Search, ChevronDown, ChevronUp, Plus, User} from "lucide-react";
import { useAuth } from "@workos-inc/authkit-react";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import api from "../http";
import { toast } from "react-toastify";
import SettingsDropdown from "../components/assistant/SettingsDropdown";
import ProfileImageModal from "../components/assistant/ProfileImageModal";
import { useColor } from "@/contexts/ColorContext";
import { appConfig } from "@/appConfig";

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  profile_picture_url: string | null;
  role: string | null;
  created_at: string;
}

export default function Users() {
  const { getAccessToken, user, role: currentUserRole } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    organization_id: import.meta.env.VITE_ORGANIZATION_ID,
    role: "member",
  });

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState(1);
  const [userFilter, setUserFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [totalUsers, setTotalUsers] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const { themeColor } = useColor();
  const APP_COLOR = appConfig.features.isDemoMode
    ? themeColor
    : appConfig.themeColor;

  // Current user state for settings dropdown
  const [currentUser, setCurrentUser] = useState<{
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  } | null>(null);

  // Profile image modal state
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // Load current user information
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const token = await getAccessToken();
        const userData = await api.getCurrentUser(token);
        setCurrentUser(userData);
      } catch (error) {
        console.error("Failed to load current user:", error);
      }
    };

    if (user) {
      loadCurrentUser();
    }
  }, [user, getAccessToken]);

  const handleProfileImageUpdate = (imageUrl: string) => {
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        profile_image_url: imageUrl,
      });

      // Also update the user in the users list if they exist there
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === currentUser.id
            ? { ...user, profile_picture_url: imageUrl }
            : user,
        ),
      );
    }
  };

  const handleUserInfoUpdate = (updatedUser: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  }) => {
    setCurrentUser(updatedUser);

    // Also update the user in the users list if they exist there
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.id === updatedUser.id
          ? {
              ...user,
              first_name: updatedUser.first_name || user.first_name,
              last_name: updatedUser.last_name || user.last_name,
              profile_picture_url: (updatedUser.profile_image_url ?? null) as
                | string
                | null,
            }
          : user,
      ),
    );
  };


  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const token = await getAccessToken();
      const offset = (currentPage - 1) * 10;
      const data = await api.getUsers(token, offset, 10, userFilter, sortField, sortDirection);

      if (data && data.users && data.users.data) {
        setUsers(data.users.data);
        setTotalPages(data.users.total_pages || 1);
        setTotalUsers(data.users.total_users || 0);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (field: string, direction: 'asc' | 'desc') => {
    setSortField(field);
    setSortDirection(direction);
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, userFilter, sortField, sortDirection]);

  const handleDelete = async (userId: string) => {
    try {
      const token = await getAccessToken();
      const result = await api.deleteUser(userId, token);
      if (result) {
        setUsers(users.filter((user) => user.id !== userId));
        setTotalUsers(totalUsers - 1); // Optimistic update
        toast.success("User deleted successfully", {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("Failed to delete user", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      // Check if this is the last admin and trying to change to member
      const adminCount = users.filter(u => u.role === "admin").length;
      const userBeingChanged = users.find(u => u.id === userId);
      
      if (userBeingChanged?.role === "admin" && newRole === "member" && adminCount === 1) {
        toast.error("Sie können die Rolle des letzten Administrators nicht ändern", {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        return;
      }

      const token = await getAccessToken();
      await api.updateUserRole(userId, newRole, token);
      
      // Update local state
      setUsers(users.map(u => 
        u.id === userId ? { ...u, role: newRole } : u
      ));
      
      toast.success("Rolle erfolgreich geändert. Benutzer muss sich erneut anmelden.", {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } catch (error: any) {
      console.error("Error updating user role:", error);
      
      // Check for specific error messages
      const errorMessage = error.message?.includes("Cannot change your own role")
        ? "Sie können Ihre eigene Rolle nicht ändern"
        : "Fehler beim Ändern der Rolle";
      
      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = await getAccessToken();
      const response = await api.addUser(formData, token);
      toast.success("User has been added successfully!", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      const newUser: User = {
        id: response.user.id,
        first_name: response.user.first_name,
        last_name: response.user.last_name,
        email: response.user.email,
        profile_picture_url: null,
        role: formData.role,
        created_at: response.user.created_at,
      };
      setUsers([...users, newUser]);
      setTotalUsers(totalUsers + 1); // Optimistic update
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        organization_id: import.meta.env.VITE_ORGANIZATION_ID,
        role: "member",
      });
      setShowModal(false);
    } catch (error) {
      console.error("Error adding user:", error);
      toast.error("Failed to add user.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-transparent admindiv">
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header - hidden on mobile */}
        <header className="hidden lg:flex items-center justify-between px-8 py-4 border-b border-gray-200">
          <h1 className="text-xl font-semibold">Benutzer</h1>
          <div className="flex items-center gap-2">
            <SettingsDropdown
              onOpenProfileModal={() => setIsProfileModalOpen(true)}
              currentUser={currentUser}
            />
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 p-4 lg:p-8 overflow-auto">
          {/* Mobile Header - only show on mobile */}
          <div className="lg:hidden mb-6">
            <h1 className="text-xl font-semibold mb-4">Benutzer</h1>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Alle Benutzer</span>
                <div className="flex items-center bg-[#EEF2FF] rounded-[6px] px-[6px] py-[3px]">
                  <span
                    className={`font-bold text-xs`}
                    style={{ color: APP_COLOR }}
                  >
                    {totalUsers} {totalUsers === 1 ? "Benutzer" : "Benutzer"}
                  </span>
                </div>
              </div>
              <button
                className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-white h-8 px-3 py-1"
                style={{ backgroundColor: APP_COLOR }}
                onClick={() => setShowModal(true)}
              >
                <Plus size={14} className="mr-1" />
                <span className="text-xs">Hinzufügen</span>
              </button>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <span className="text-lg font-medium">Alle Benutzer</span>
              <div className="flex items-center bg-[#EEF2FF] rounded-[6px] px-[6px] py-[3px]">
                <span
                  className={`font-bold text-xs`}
                  style={{ color: APP_COLOR }}
                >
                  {totalUsers} {totalUsers === 1 ? "Benutzer" : "Benutzer"}
                </span>
              </div>
            </div>

            <div className="relative w-[300px]">
              <input
                type="text"
                placeholder="Benutzer suchen..."
                className="w-full pl-4 pr-10 py-2 border border-gray-200 rounded-lg"
                value={searchInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchInput(value);
                  // Only filter if 3+ chars or empty
                  if (value.length >= 3 || value.length === 0) {
                    setUserFilter(value);
                    setCurrentPage(1); // reset page
                  }
                }}
              />
              <button className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-gray-400 hover:text-gray-600 transition-colors">
                <Search size={18} />
              </button>
            </div>

            <div className="flex items-center gap-4">
              <button
                className={`inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-white h-10 px-4 py-2`}
                style={{ backgroundColor: APP_COLOR }}
                onClick={() => setShowModal(true)}
              >
                <Plus size={16} className="mr-2" />
                <span>Neuen Benutzer hinzufügen</span>
              </button>
            </div>
          </div>

          {/* Mobile Search - only show on mobile */}
          <div className="lg:hidden mb-4">
            <div className="relative w-full">
              <input
                type="text"
                placeholder="Benutzer suchen..."
                className="w-full pl-4 pr-10 py-2 border border-gray-200 rounded-lg"
                value={searchInput}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchInput(value);
                  // Only filter if 3+ chars or empty
                  if (value.length >= 3 || value.length === 0) {
                    setUserFilter(value);
                    setCurrentPage(1); // reset page
                  }
                }}
              />
              <button className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-gray-400 hover:text-gray-600 transition-colors">
                <Search size={18} />
              </button>
            </div>
          </div>

          {/* Mobile User Cards */}
          <div className="lg:hidden">
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">
                Benutzer werden geladen...
              </div>
            ) : users.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Keine Benutzer gefunden
              </div>
            ) : (
              <div className="space-y-3">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="bg-white border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarImage
                            src={user.profile_picture_url || undefined}
                            alt="User avatar"
                          />
                          <AvatarFallback>
                            {user.first_name && user.last_name
                              ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase()
                              : user.first_name?.charAt(0)?.toUpperCase() ||
                                user.last_name?.charAt(0)?.toUpperCase() ||
                                user.email.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium text-sm">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {user.email}
                          </div>
                          <div className="mt-1">
                            <select
                              value={user.role || "member"}
                              onChange={(e) => handleRoleChange(user.id, e.target.value)}
                              disabled={user.id === currentUser?.id}
                              className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer transition-colors ${
                                user.role === "superadmin"
                                  ? "bg-purple-100 text-purple-800 hover:bg-purple-200"
                                  : user.role === "admin" 
                                    ? "bg-red-100 text-red-800 hover:bg-red-200" 
                                    : "bg-blue-100 text-blue-800 hover:bg-blue-200"
                              } ${user.id === currentUser?.id ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                              <option value="member" className="bg-white text-black">Mitglied</option>
                              <option value="admin" className="bg-white text-black">Administrator</option>
                              {currentUserRole === "superadmin" && (
                                <option value="superadmin" className="bg-white text-black">Superadmin</option>
                              )}
                            </select>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-red-500 hover:text-red-700 transition-colors flex-shrink-0"
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3 6H5H21M19 6V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6M8 6V4C8 2.89543 8.89543 2 10 2H14C15.1046 2 16 2.89543 16 4V6"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                    <div className="text-xs text-gray-500">
                      Hinzugefügt:{" "}
                      {user.created_at
                        ? new Date(user.created_at).toLocaleString("de-DE", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          })
                        : "N/A"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Desktop Table Container */}
          <div className="hidden lg:block bg-white border border-gray-200 rounded-2xl overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-[2fr_2fr_1fr_2fr_auto] bg-gray-50 text-gray-700 border-b border-gray-200">
              <div className="p-4 font-medium flex items-center gap-1">
                Name <div className="flex flex-col">
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('name', 'asc')}>
                    <ChevronUp size={16} className={sortField === 'name' && sortDirection === 'asc' ? "text-blue-600" : "text-gray-400"} />
                  </div>
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('name', 'desc')}>
                    <ChevronDown size={16} className={sortField === 'name' && sortDirection === 'desc' ? "text-blue-600" : "text-gray-400"} />
                  </div>
                </div>
              </div>
              <div className="p-4 font-medium flex items-center gap-1">
                Email <div className="flex flex-col">
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('email', 'asc')}>
                    <ChevronUp size={16} className={sortField === 'email' && sortDirection === 'asc' ? "text-blue-600" : "text-gray-400"} />
                  </div>
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('email', 'desc')}>
                    <ChevronDown size={16} className={sortField === 'email' && sortDirection === 'desc' ? "text-blue-600" : "text-gray-400"} />
                  </div>
                </div>
              </div>
              <div className="p-4 font-medium flex items-center gap-1">
                Rolle <ChevronDown size={16} className="text-gray-400" />
              </div>
              <div className="p-4 font-medium flex items-center gap-1">
                Hinzugefügt am{" "}
                <div className="flex flex-col">
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('date', 'asc')}>
                    <ChevronUp size={16} className={sortField === 'date' && sortDirection === 'asc' ? "text-blue-600" : "text-gray-400"} />
                  </div>
                  <div className="cursor-pointer hover:bg-gray-200 rounded px-1 py-0" onClick={() => handleSort('date', 'desc')}>
                    <ChevronDown size={16} className={sortField === 'date' && sortDirection === 'desc' ? "text-blue-600" : "text-gray-400"} />
                  </div>
                </div>
              </div>
              <div className="p-4 font-medium">Aktionen</div>
            </div>

            {/* Table Rows */}
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">
                Benutzer werden geladen...
              </div>
            ) : users.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                Keine Benutzer gefunden
              </div>
            ) : (
              users.map((user) => (
                <div
                  key={user.id}
                  className="grid grid-cols-[2fr_2fr_1fr_2fr_auto] border-b border-gray-200"
                >
                  <div className="p-4 flex items-center gap-2">
                    <Avatar className="w-8 h-8">
                      <AvatarImage
                        src={user.profile_picture_url || undefined}
                        alt="User avatar"
                      />
                      <AvatarFallback>
                        {user.first_name && user.last_name
                          ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase()
                          : user.first_name?.charAt(0)?.toUpperCase() ||
                            user.last_name?.charAt(0)?.toUpperCase() ||
                            user.email.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span>
                      {user.first_name} {user.last_name}
                    </span>
                  </div>
                  <div className="p-4 flex items-start break-all overflow-hidden">{user.email}</div>
                  <div className="p-4 flex items-center">
                    <select
                      value={user.role || "member"}
                      onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      disabled={user.id === currentUser?.id}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border-0 cursor-pointer transition-colors ${
                        user.role === "superadmin"
                          ? "bg-purple-100 text-purple-800 hover:bg-purple-200"
                          : user.role === "admin" 
                            ? "bg-red-100 text-red-800 hover:bg-red-200" 
                            : "bg-blue-100 text-blue-800 hover:bg-blue-200"
                      } ${user.id === currentUser?.id ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <option value="member" className="bg-white text-black">Mitglied</option>
                      <option value="admin" className="bg-white text-black">Administrator</option>
                      {currentUserRole === "superadmin" && (
                        <option value="superadmin" className="bg-white text-black">Superadmin</option>
                      )}
                    </select>
                  </div>
                  <div className="p-4 flex items-center text-gray-600">
                    {user.created_at
                      ? new Date(user.created_at).toLocaleString("en-US", {
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })
                      : "N/A"}
                  </div>
                  <div className="p-4 flex items-center">
                    <button
                      onClick={() => handleDelete(user.id)}
                      className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-red-500 hover:text-red-700 transition-colors"
                    >
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3 6H5H21M19 6V20C19 21.1046 18.1046 22 17 22H7C5.89543 22 5 21.1046 5 20V6M8 6V4C8 2.89543 8.89543 2 10 2H14C15.1046 2 16 2.89543 16 4V6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            )}

            {/* Pagination (Within Table) */}
            <div className="flex items-center justify-between p-4 bg-gray-50">
              {/* Left side: arrows + page numbers */}
              <div className="flex items-center gap-5">
                {/* Previous button */}
                <button
                  type="button"
                  onClick={() => {
                    if (currentPage > 1) {
                      setCurrentPage(currentPage - 1);
                    }
                  }}
                  disabled={currentPage === 1}
                  className="text-600 disabled:opacity-50 hover:text-gray-700 hover:bg-slate-100 rounded-md px-3 py-1 transition-colors bg-transparent hover:border-transparent"
                >
                  &lt; Zurück
                </button>

                {/* Page Numbers */}
                <div className="flex items-center gap-3">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <span
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`cursor-pointer ${
                          page === currentPage
                            ? "w-8 h-8 flex items-center justify-center rounded-full"
                            : "text-gray-700 hover:text-gray-900"
                        }`}
                        style={
                          page === currentPage
                            ? {
                                color: APP_COLOR,
                                backgroundColor: `${APP_COLOR}20`,
                              }
                            : undefined
                        }
                      >
                        {page}
                      </span>
                    ),
                  )}
                </div>

                {/* Next button */}
                <button
                  type="button"
                  onClick={() => {
                    if (currentPage < totalPages) {
                      setCurrentPage(currentPage + 1);
                    }
                  }}
                  disabled={currentPage === totalPages}
                  className="text-600 disabled:opacity-50 hover:text-gray-700 hover:bg-slate-100 rounded-md px-3 py-1 transition-colors bg-transparent hover:border-transparent"
                >
                  Weiter &gt;
                </button>
              </div>

              {/* Right side: "Showing x of y results" */}
              <div className="text-sm text-gray-500">
                Zeige {users.length} von {totalUsers} Ergebnissen
              </div>
            </div>
          </div>

          {/* Mobile Pagination */}
          <div className="lg:hidden mt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (currentPage > 1) {
                      setCurrentPage(currentPage - 1);
                    }
                  }}
                  disabled={currentPage === 1}
                  className="text-sm text-gray-600 disabled:opacity-50 hover:text-gray-700 px-3 py-1 rounded-md transition-colors"
                >
                  &lt; Zurück
                </button>
                <span className="text-sm text-gray-500">
                  Seite {currentPage} von {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    if (currentPage < totalPages) {
                      setCurrentPage(currentPage + 1);
                    }
                  }}
                  disabled={currentPage === totalPages}
                  className="text-sm text-gray-600 disabled:opacity-50 hover:text-gray-700 px-3 py-1 rounded-md transition-colors"
                >
                  Weiter &gt;
                </button>
              </div>
              <div className="text-xs text-gray-500">
                {users.length} von {totalUsers}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-[500px] relative max-h-[90vh] overflow-y-auto">
            <div className="p-4 lg:p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">
                  Neuen Benutzer hinzufügen
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-md bg-transparent border-none focus:outline-none active:outline-none text-black hover:text-gray-700 transition-colors"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <label htmlFor="first_name" className="text-sm font-medium">
                      Vorname
                    </label>
                    <input
                      type="text"
                      id="first_name"
                      name="first_name"
                      placeholder="Vorname"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-200 rounded-lg"
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <label htmlFor="last_name" className="text-sm font-medium">
                      Nachname
                    </label>
                    <input
                      type="text"
                      id="last_name"
                      name="last_name"
                      placeholder="Nachname"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-200 rounded-lg"
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <label htmlFor="email" className="text-sm font-medium">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="E-Mail"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-200 rounded-lg"
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <label htmlFor="role" className="text-sm font-medium">
                      Rolle
                    </label>
                    <select
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={(e) => handleInputChange(e as any)}
                      className="w-full p-2 border border-gray-200 rounded-lg bg-white"
                      required
                    >
                      <option value="member">Mitglied</option>
                      <option value="admin">Administrator</option>
                      {currentUserRole === "superadmin" && (
                        <option value="superadmin">Superadmin</option>
                      )}
                    </select>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-gray-300 bg-transparent hover:bg-slate-100 text-gray-700 hover:text-gray-700 h-10 px-4 py-2 order-2 sm:order-1"
                    onClick={() => setShowModal(false)}
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center whitespace-nowrap rounded-[1234px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-white h-10 px-4 py-2 order-1 sm:order-2"
                    style={{ backgroundColor: APP_COLOR }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = APP_COLOR;
                      e.currentTarget.style.filter = 'brightness(0.9)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = APP_COLOR;
                      e.currentTarget.style.filter = 'none';
                    }}
                  >
                    Benutzer hinzufügen
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Profile Image Modal */}
      <ProfileImageModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        currentUser={currentUser}
        onProfileImageUpdate={handleProfileImageUpdate}
        onUserInfoUpdate={handleUserInfoUpdate}
      />
    </div>
  );
}
